import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Input, Label, Modal } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { DriverAutocomplete } from '@/components/common/DriverAutocomplete'
import { DateInput } from '@/components/ui/DateInput'
import { usePaymentsStore } from '@/store/payments.store'
import type { Driver } from '@/types/driver'
import type { SettlementItem } from '@/types/settlement'
import { numericAndNegativeOnly, numericOnly } from '@/utils/input'

const getPaymentSchema = (isInitialBalance: boolean) => z.object({
  driverId: z.string().min(1, 'Выберите водителя'),
  date: z.string().min(1, 'Укажите дату'),
  amount: z.number({
    error: 'Укажите сумму',
  }).refine(val => val !== 0, { message: 'Укажите сумму' })
    .refine(val => isInitialBalance || val > 0, { message: 'Сумма должна быть больше 0' }),
})

type FormValues = {
  driverId: string;
  date: string;
  amount: number;
};

interface PaymentDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  driverId?: string;
  paymentId?: string;
  onSuccess?: () => void;
  isInitialBalance?: boolean;
}

interface DriverSelectorProps {
  value: string;
  onChange: (val: string) => void;
  isInvalid: boolean;
}

function DriverSelector({ value, onChange, isInvalid }: DriverSelectorProps) {
  const handleSelectionChange = useCallback((driver: Driver | null) => {
    onChange(driver?.ID ?? '')
  }, [onChange])

  return (
    <DriverAutocomplete
      selectedId={value}
      onSelectionChange={handleSelectionChange}
      isInvalid={isInvalid}
    />
  )
}

export default function PaymentDialog({
  isOpen,
  onOpenChange,
  driverId: propDriverId,
  paymentId,
  onSuccess,
  isInitialBalance = false,
}: PaymentDialogProps) {
  const { t } = useTranslation()
  const { addPayment, updatePayment, addInitialBalance, updateInitialBalance, currentSettlement } = usePaymentsStore()

  const editingPaymentItem = paymentId && currentSettlement
    ? currentSettlement.Items.find((item: SettlementItem) => item.ID === paymentId)
    : null

  const isInitialBalanceMode = isInitialBalance || (editingPaymentItem?.Type === 'opening_balance')

  const editingPayment = useMemo(() => {
    if (!editingPaymentItem) return null
    if (editingPaymentItem.Type === 'payment') {
      return {
        id: editingPaymentItem.ID,
        driverId: propDriverId ?? '',
        date: editingPaymentItem.Date,
        amount: editingPaymentItem.Amount,
      }
    } else if (editingPaymentItem.Type === 'opening_balance') {
      return {
        id: editingPaymentItem.ID,
        driverId: propDriverId ?? '',
        date: editingPaymentItem.StartDate,
        amount: editingPaymentItem.Total,
      }
    }
    return null
  }, [editingPaymentItem, propDriverId])

  const isDriverPreselected = Boolean(propDriverId) || Boolean(editingPayment)

  const getTodayDateString = () => {
    const today = new Date()
    const yyyy = today.getFullYear()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }

  const [amountInput, setAmountInput] = useState(() => {
    const val = editingPayment?.amount
    return val !== undefined && val !== null && val !== 0 ? String(val).replace('.', ',') : ''
  })

  const schema = useMemo(() => getPaymentSchema(Boolean(isInitialBalanceMode)), [isInitialBalanceMode])

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      driverId: editingPayment?.driverId ?? propDriverId ?? '',
      date: editingPayment?.date ?? (isInitialBalanceMode ? '2026-01-01' : getTodayDateString()),
      amount: editingPayment?.amount !== undefined && editingPayment?.amount !== 0 ? editingPayment.amount : undefined,
    },
  })

  // Reset form when modal opens or editingPayment changes
  useEffect(() => {
    if (isOpen) {
      const val = editingPayment?.amount
      const formatted = val !== undefined && val !== null && val !== 0 ? String(val).replace('.', ',') : ''
      setAmountInput(formatted)

      reset({
        driverId: editingPayment?.driverId ?? propDriverId ?? '',
        date: editingPayment?.date ?? (isInitialBalanceMode ? '2026-01-01' : getTodayDateString()),
        amount: val !== undefined && val !== null && val !== 0 ? val : undefined,
      })
    }
  }, [isOpen, propDriverId, editingPayment, reset, isInitialBalanceMode])

  const onSubmit = async (data: FormValues) => {
    try {
      if (isInitialBalanceMode) {
        if (paymentId) {
          await updateInitialBalance(data.driverId, paymentId, {
            date: data.date,
            amount: data.amount,
          })
        } else {
          await addInitialBalance(data.driverId, {
            date: data.date,
            amount: data.amount,
          })
        }
      } else {
        if (paymentId) {
          await updatePayment(data.driverId, paymentId, {
            date: data.date,
            amount: data.amount,
          })
        } else {
          await addPayment(data.driverId, {
            date: data.date,
            amount: data.amount,
          })
        }
      }
      if (onSuccess) {
        onSuccess()
      }
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save payment:', err)
    }
  }

  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const renderDateInput = useCallback(({ field }: { field: { value: string; onChange: (val: string) => void } }) => (
    <DateInput
      label={t('settlements.date')}
      value={field.value}
      onChange={field.onChange}
      isRequired
      isInvalid={Boolean(errors.date)}
    />
  ), [t, errors.date])

  const renderDriverAutocomplete = useCallback(({ field }: { field: { value: string; onChange: (val: string) => void } }) => (
    <DriverSelector
      value={field.value}
      onChange={field.onChange}
      isInvalid={Boolean(errors.driverId)}
    />
  ), [errors.driverId])

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop variant="blur" isDismissable>
        <Modal.Container>
          <Modal.Dialog>
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading className="font-bold">
                {paymentId
                  ? (isInitialBalanceMode
                      ? t('settlements.initial_balance_dialog_edit_title')
                      : t('settlements.payment_dialog_edit_title'))
                  : (isInitialBalanceMode
                      ? t('settlements.initial_balance_dialog_title')
                      : t('settlements.payment_dialog_title'))}
              </Modal.Heading>
            </Modal.Header>
            <Modal.Body className="mt-4">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1">
                {!isDriverPreselected && (
                  <div className="flex flex-col gap-1">
                    <Controller
                      name="driverId"
                      control={control}
                      render={renderDriverAutocomplete}
                    />
                    {errors.driverId?.message && (
                      <span className="text-xs text-danger">{errors.driverId.message}</span>
                    )}
                  </div>
                )}

                {!isInitialBalanceMode && (
                  <div className="flex flex-col gap-1">
                    <Controller
                      name="date"
                      control={control}
                      render={renderDateInput}
                    />
                    {errors.date?.message && (
                      <span className="text-xs text-danger">{errors.date.message}</span>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-1">
                  <Label htmlFor="payment-amount" isRequired>
                    {t('settlements.amount')}
                  </Label>
                  <Controller
                    name="amount"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="payment-amount"
                        ref={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                        value={amountInput}
                        type="text"
                        onKeyDown={isInitialBalanceMode ? numericAndNegativeOnly : numericOnly}
                        placeholder={isInitialBalanceMode ? "0.00" : "1500.00"}
                        variant="secondary"
                        className={errors.amount ? 'border-danger animate-shake' : ''}
                        onChange={e => {
                          const valStr = e.target.value.replace('.', ',')
                          setAmountInput(valStr)
                          const parsedVal = Number(valStr.replace(',', '.'))
                          const finalVal = isNaN(parsedVal) ? 0 : parsedVal
                          field.onChange(finalVal)
                        }}
                      />
                    )}
                  />
                  {errors.amount?.message && (
                    <span className="text-xs text-danger">{errors.amount.message}</span>
                  )}
                </div>

                <div className="flex gap-3 pt-4 justify-end">
                  <Button variant="ghost" onClick={handleClose}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" isDisabled={isSubmitting}>
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {paymentId ? t('common.save') : t('common.add')}
                      </div>
                    ) : (
                      paymentId ? t('common.save') : t('common.add')
                    )}
                  </Button>
                </div>
              </form>
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}
